import { Lead, GuideSection, H3, P, UL, OL, LI, GuideLink } from './guide-ui';

/**
 * Guide en français (locale: 'fr', logpulse.fr) : NIS2 et l'état de la
 * transposition française (projet de loi résilience des infrastructures
 * critiques et renforcement de la cybersécurité, ANSSI, référentiels).
 * Volontairement un contenu local original, pas une traduction du guide
 * NIS2 anglais : celui-ci traite la directive elle-même et reste
 * accessible via les related links pour approfondir.
 */
export function Nis2FranceGuideBody() {
  return (
    <>
      <Lead>
        NIS2 est la directive européenne de cybersécurité qui devait être
        transposée avant le 17 octobre 2024. En France, la loi de
        transposition n&rsquo;est toujours pas définitivement adoptée, et
        la Commission européenne a saisi la Cour de justice de
        l&rsquo;UE. Pourtant, environ 15 000 entités françaises seront
        concernées, et les exigences de la directive, elles, sont déjà
        connues. Ce guide fait le point : état de la transposition, qui
        est concerné, obligations à venir, et ce que cela signifie
        concrètement pour le logging et le monitoring.
      </Lead>

      <GuideSection id="ou-en-est-la-france" title="Où en est la transposition française ?">
        <P>
          NIS2 (directive (UE) 2022/2555) succède à la première directive
          NIS et élargit massivement le champ des entités régulées. La
          France la transpose via le projet de loi{' '}
          <strong>
            relatif à la résilience des infrastructures critiques et au
            renforcement de la cybersécurité
          </strong>
          , qui regroupe trois volets : REC (résilience des entités
          critiques), NIS2 et DORA pour le secteur financier.
        </P>
        <P>
          Le calendrier parlementaire s&rsquo;étire : le Sénat a adopté le
          texte en première lecture le 12 mars 2025, puis la commission
          spéciale de l&rsquo;Assemblée nationale a adopté une version
          amendée le 10 septembre 2025. À ce jour (août 2026),{' '}
          <strong>la loi n&rsquo;est toujours pas définitivement adoptée ni
          promulguée</strong>. Ce retard a un prix : le 8 juillet 2026, la
          Commission européenne a{' '}
          <strong>saisi la Cour de justice de l&rsquo;UE</strong> contre la
          France pour non-transposition de NIS2, en demandant des
          sanctions financières.
        </P>
        <P>
          Le piège serait d&rsquo;attendre « la date ». Les obligations
          arriveront quoi qu&rsquo;il en soit, et leur contenu est déjà
          fixé au niveau européen : gestion des risques (dont logging et
          détection), notification des incidents, responsabilité de la
          direction, sanctions. Chaque mois d&rsquo;attente est un mois de
          moins pour construire ce qui prend du temps : la visibilité sur
          son propre système d&rsquo;information.
        </P>
      </GuideSection>

      <GuideSection id="suis-je-concerne" title="Suis-je concerné ?">
        <P>
          La directive distingue deux catégories, avec un régime de
          contrôle et des sanctions différents :
        </P>
        <UL>
          <LI>
            <strong>Entités essentielles</strong> : grandes organisations
            dans des secteurs comme l&rsquo;énergie, les transports, le
            secteur bancaire, les infrastructures des marchés financiers,
            la santé, l&rsquo;eau potable et les eaux usées,
            l&rsquo;infrastructure numérique, l&rsquo;administration
            publique et le spatial.
          </LI>
          <LI>
            <strong>Entités importantes</strong> : organisations moyennes
            et grandes, entre autres dans les services postaux, la gestion
            des déchets, la chimie, l&rsquo;agroalimentaire,
            l&rsquo;industrie manufacturière (dont dispositifs médicaux et
            électronique), les fournisseurs numériques et la recherche.
          </LI>
        </UL>
        <P>
          Le seuil de taille se situe en principe autour de 50 salariés ou
          10 millions d&rsquo;euros de chiffre d&rsquo;affaires annuel. Des
          structures plus petites peuvent néanmoins entrer dans le champ,
          par exemple en tant que fournisseur unique d&rsquo;un service
          essentiel. Au total, environ{' '}
          <strong>15 000 entités françaises</strong> devraient entrer dans
          le champ, dont beaucoup d&rsquo;ETI et de PME qui ne se sont
          jamais vues comme « critiques » : un changement d&rsquo;échelle
          par rapport aux quelques centaines d&rsquo;opérateurs régulés
          jusqu&rsquo;ici.
        </P>
      </GuideSection>

      <GuideSection id="obligations" title="Les obligations : gestion des risques et gouvernance">
        <P>
          Le cœur de la directive : des mesures techniques et
          organisationnelles proportionnées pour maîtriser les risques
          pesant sur les réseaux et systèmes d&rsquo;information. La
          directive cite expressément, entre autres :
        </P>
        <UL>
          <LI>l&rsquo;analyse des risques et les politiques de sécurité ;</LI>
          <LI>
            <strong>la gestion des incidents</strong> : détecter, analyser,
            contenir, rétablir, et pouvoir le démontrer ;
          </LI>
          <LI>la continuité d&rsquo;activité, les sauvegardes et la gestion de crise ;</LI>
          <LI>la sécurité de la chaîne d&rsquo;approvisionnement ;</LI>
          <LI>la sécurité du développement, de l&rsquo;acquisition et de la maintenance ;</LI>
          <LI>des procédures pour mesurer l&rsquo;efficacité des mesures ;</LI>
          <LI>l&rsquo;hygiène informatique et la formation ;</LI>
          <LI>la cryptographie et, là où c&rsquo;est pertinent, le chiffrement ;</LI>
          <LI>le contrôle d&rsquo;accès, la sécurité des ressources humaines et la gestion des actifs ;</LI>
          <LI>l&rsquo;authentification multifacteur et les communications sécurisées.</LI>
        </UL>
        <P>
          S&rsquo;y ajoute la{' '}
          <strong>responsabilité des organes de direction</strong> : la
          direction doit approuver les mesures, superviser leur mise en
          œuvre et se former à la cybersécurité. En cas de manquement
          grave, sa responsabilité peut être engagée.
        </P>
        <H3>Le calendrier annoncé par l&rsquo;ANSSI</H3>
        <P>
          L&rsquo;ANSSI a annoncé que les entités disposeront
          d&rsquo;environ <strong>trois ans à compter de la publication des
          référentiels techniques</strong> pour se mettre pleinement en
          conformité. La trajectoire attendue : au quatrième trimestre
          2026, analyse des risques et plans de sécurisation ; en 2027,
          déploiement de mesures concrètes comme l&rsquo;authentification
          multifacteur et les sauvegardes. Trois ans paraissent longs,
          mais cartographier son SI et bâtir une vraie capacité de
          détection ne se fait pas en un trimestre.
        </P>
      </GuideSection>

      <GuideSection id="notification-des-incidents" title="La notification des incidents : 24 heures, 72 heures, un mois">
        <P>
          Les incidents significatifs devront être notifiés en trois
          étapes, un mécanisme fixé par la directive elle-même :
        </P>
        <OL>
          <LI>
            <strong>Sous 24 heures</strong> : une alerte précoce, avec une
            première appréciation indiquant si un acte illicite ou
            malveillant est suspecté et si l&rsquo;incident peut avoir un
            impact transfrontalier.
          </LI>
          <LI>
            <strong>Sous 72 heures</strong> : une notification complète
            avec une première évaluation de la gravité et de
            l&rsquo;impact, et, lorsqu&rsquo;ils sont disponibles, les
            indicateurs de compromission (IOC).
          </LI>
          <LI>
            <strong>Sous un mois</strong> : un rapport final décrivant en
            détail l&rsquo;incident, sa cause probable, les mesures prises
            et l&rsquo;éventuel impact transfrontalier.
          </LI>
        </OL>
        <P>
          Ces délais sont courts. Commencer à collecter des logs après
          l&rsquo;incident, c&rsquo;est les manquer : dire quelque chose
          de solide sur un acte malveillant en 24 heures suppose que les
          logs pertinents existent déjà, qu&rsquo;ils soient
          interrogeables, et que la détection ait remarqué
          l&rsquo;incident en premier lieu.
        </P>
      </GuideSection>

      <GuideSection id="controle-et-sanctions" title="Contrôle et sanctions : le rôle de l&rsquo;ANSSI">
        <P>
          L&rsquo;<strong>ANSSI</strong> sera l&rsquo;autorité compétente
          pour NIS2 en France ; le secteur financier relève de
          l&rsquo;ACPR et de l&rsquo;AMF via DORA. Les entités essentielles
          feront l&rsquo;objet d&rsquo;un contrôle proactif (audits et
          inspections), les entités importantes d&rsquo;un contrôle
          réactif, après signalement ou incident.
        </P>
        <P>Les plafonds de sanction sont fixés par la directive :</P>
        <UL>
          <LI>
            entités essentielles : jusqu&rsquo;à{' '}
            <strong>10 millions d&rsquo;euros ou 2 %</strong> du chiffre
            d&rsquo;affaires annuel mondial ;
          </LI>
          <LI>
            entités importantes : jusqu&rsquo;à{' '}
            <strong>7 millions d&rsquo;euros ou 1,4 %</strong> du chiffre
            d&rsquo;affaires annuel mondial.
          </LI>
        </UL>
        <P>
          Les modalités exactes seront précisées par la loi française,
          mais l&rsquo;ordre de grandeur, comparable au RGPD, est déjà
          connu.
        </P>
      </GuideSection>

      <GuideSection id="logging-et-monitoring" title="Ce que cela signifie concrètement pour le logging et le monitoring">
        <P>
          Le logging et le monitoring traversent presque toutes les
          obligations : sans les bons logs, impossible de détecter un
          incident, d&rsquo;étayer une alerte en 24 heures ou de rédiger un
          rapport final. Concrètement :
        </P>
        <H3>Centraliser les logs</H3>
        <P>
          Les logs des serveurs, équipements réseau, fournisseurs
          d&rsquo;identité, services cloud et applications doivent
          converger vers un système interrogeable. Reconstituer un incident
          à travers dix systèmes isolés ne tient pas en 72 heures.
        </P>
        <H3>Les conserver assez longtemps, de manière démontrable</H3>
        <P>
          La directive ne fixe pas de durée de rétention : le critère est
          de pouvoir enquêter sur les incidents et les notifier. En
          pratique, 6 à 18 mois sont courants, avec une couche
          interrogeable à chaud et une archive moins coûteuse. Documenter
          ce choix et sa justification, c&rsquo;est exactement ce
          qu&rsquo;une autorité de contrôle demandera.
        </P>
        <H3>Surveiller en temps réel, pas rétrospectivement</H3>
        <P>
          Les délais de notification supposent de voir les incidents
          pendant qu&rsquo;ils se produisent. Cela exige des règles de
          détection sur les logs (échecs de connexion, comportements
          anormaux, connexions réseau suspectes) et une alerte qui atteint
          un humain à temps, sans le noyer sous les faux positifs.
        </P>
        <H3>Tenir une piste d&rsquo;audit</H3>
        <P>
          Qui a fait quoi, et quand, y compris dans la plateforme de logs
          elle-même ? Une piste d&rsquo;audit infalsifiable étaye à la fois
          les notifications d&rsquo;incident et la redevabilité vis-à-vis
          de l&rsquo;autorité de contrôle.
        </P>
        <P>
          Soyons clairs : aucun produit ne rend une organisation conforme
          à lui seul, NIS2 exige aussi une gouvernance, des processus et
          de la formation. Ce que LogPulse couvre, c&rsquo;est le versant
          logging, détection et monitoring : gestion centralisée des logs
          avec rétention configurable, plus de 50 détections intégrées
          alignées sur MITRE ATT&amp;CK via le{' '}
          <GuideLink href="/siem">SIEM LogPulse</GuideLink>, alertes basées
          sur le risque, et rapports de conformité reliant détections et
          données aux exigences NIS2. Toutes les données restent dans
          l&rsquo;UE (GCP Amsterdam), ce qui simplifie les contrats de
          traitement et la redevabilité.
        </P>
      </GuideSection>

      <GuideSection id="par-ou-commencer" title="Par où commencer, sans attendre la loi">
        <OL>
          <LI>
            <strong>Déterminer si ton organisation est concernée</strong>{' '}
            et dans quelle catégorie, sur la base des secteurs et seuils de
            la directive.
          </LI>
          <LI>
            <strong>Impliquer la direction</strong> : elle portera la
            responsabilité et doit approuver la démarche.
          </LI>
          <LI>
            <strong>Mener une analyse des risques</strong> et cartographier
            les systèmes et fournisseurs critiques, la première étape
            attendue par l&rsquo;ANSSI.
          </LI>
          <LI>
            <strong>Centraliser le logging et mettre en place la
            détection</strong> : le socle commun de la gestion des risques
            et de la notification des incidents.
          </LI>
          <LI>
            <strong>Répéter la procédure de notification</strong> : qui
            alerte en 24 heures, et avec quelles informations ?
          </LI>
          <LI>
            <strong>Tout documenter</strong> : mesures, choix, durées de
            rétention, exercices. Une conformité indémontrable
            n&rsquo;existe pas aux yeux d&rsquo;une autorité de contrôle.
          </LI>
        </OL>
        <P>
          Pour voir comment LogPulse couvre le versant logging, détection
          et reporting :{' '}
          <GuideLink href="/compliance/nis2">la conformité NIS2 avec LogPulse</GuideLink>
          , et les{' '}
          <GuideLink href="/pricing">tarifs</GuideLink> pour démarrer
          gratuitement et configurer les premières détections dès
          aujourd&rsquo;hui.
        </P>
      </GuideSection>
    </>
  );
}
